import { MOBILE_GUEST_ENABLED } from "./environment";
import type {
  AccountAiProfileSettingsResponse,
  CheckinPreferencesResponse,
  InboxComposerResponse,
  InboxContactContextResponse,
  InboxConversationDetail,
  InboxConversationPage,
  InboxDraftRejectionReason,
  InboxDraftReviewResponse,
  InboxFoldersResponse,
  InboxLabelsResponse,
  InboxMessagePage,
  MessagingReviewDraftResponse,
  MessagingReviewDraftsResponse,
  MobileAuthConfigResponse,
  MobileMutationAuthorityResponse,
  MobileSession,
  MobileSignInResponse,
  MobileUiResponse,
  ProviderConnectionActionName,
  ProviderConnectionActionResponse,
  ProviderConnectionsResponse,
  SafeError,
  SocialLinksResponse,
  WynterProfileResponse,
  WctekChatResponse,
  WctekSessionListResponse,
  WctekWorkspaceResponse
} from "./types";

const DEFAULT_API_PREFIX = "/api";
const DEFAULT_TIMEOUT_MS = 20000;
const MOBILE_CLIENT = "epic-mobile-v1";
const MOBILE_MUTATION_EXEMPT_PATHS = new Set([
  "/google-login",
  "/login",
  "/login/two-factor",
  "/logout",
  "/mobile/auth/exchange",
  "/mobile/mutation-authority"
]);

type FetchLike = typeof fetch;

export interface RequestOptions extends RequestInit {
  timeoutMs?: number;
  idempotencyKey?: string;
  allowSuccessFalse?: boolean;
  skipMobileMutation?: boolean;
}

export interface ConversationListOptions {
  status?: string;
  folder?: string;
  channel?: string;
  search?: string;
  sort?: string;
  labelIds?: string[];
  view?: "conversations" | "contacts";
  cursor?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

export interface MessageListOptions {
  cursor?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

export function apiUrl(path: string, apiPrefix = DEFAULT_API_PREFIX) {
  const value = String(path || "");
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith(`${apiPrefix}/`)) return value;
  if (value.startsWith("/")) return `${apiPrefix}${value}`;
  return `${apiPrefix}/${value}`;
}

function errorField(data: any) {
  return data?.error && typeof data.error === "object" ? data.error : data;
}

export function normalizeSafeError(response: Pick<Response, "status"> | null, data: any = {}): SafeError {
  const nested = errorField(data);
  const status = Number(response?.status || data?.status || 0);
  const message =
    nested?.message ||
    (typeof data?.error === "string" ? data.error : null) ||
    data?.message ||
    "Request failed.";
  return {
    status,
    code: nested?.code || data?.code || (status === 401 ? "AUTHENTICATION_REQUIRED" : "REQUEST_FAILED"),
    message,
    retryable: nested?.retryable === true || data?.retryable === true || status >= 500,
    requiredAction: nested?.requiredAction || data?.requiredAction || null,
    correlationRef: nested?.correlationRef || data?.correlationRef || null,
    data
  };
}

function appendParams(path: string, params: Record<string, string | number | null | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  }
  const separator = path.includes("?") ? "&" : "?";
  const query = search.toString();
  return query ? `${path}${separator}${query}` : path;
}

function createSignal(timeoutMs: number, externalSignal?: AbortSignal | null) {
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const abortFromExternal = () => controller.abort();
  if (externalSignal?.aborted) controller.abort();
  else externalSignal?.addEventListener("abort", abortFromExternal, { once: true });
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    cleanup() {
      clearTimeout(timeout);
      externalSignal?.removeEventListener("abort", abortFromExternal);
    }
  };
}

export function createIdempotencyKey(scope: string, now = Date.now()) {
  const safeScope = String(scope || "mobile")
    .toLowerCase()
    .replace(/[^a-z0-9_.:-]/g, "-")
    .slice(0, 48) || "mobile";
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${safeScope}:${now}:${random}`.slice(0, 160);
}

export class EpicMobileApi {
  private readonly fetchImpl: FetchLike;
  private readonly apiPrefix: string;
  private readonly mobileGateway: boolean;
  private mutationAuthority: MobileMutationAuthorityResponse | null = null;
  private mutationAuthorityRequest: Promise<MobileMutationAuthorityResponse> | null = null;

  constructor(options: { fetchImpl?: FetchLike; apiPrefix?: string; mobileGateway?: boolean } = {}) {
    this.fetchImpl = options.fetchImpl || fetch.bind(globalThis);
    this.apiPrefix = options.apiPrefix || DEFAULT_API_PREFIX;
    this.mobileGateway = options.mobileGateway === true;
  }

  private needsMobileMutation(path: string, method: string, skipped: boolean) {
    if (!this.mobileGateway || skipped) return false;
    const normalizedMethod = String(method || "GET").toUpperCase();
    if (!["POST", "PUT", "PATCH", "DELETE"].includes(normalizedMethod)) return false;
    return !MOBILE_MUTATION_EXEMPT_PATHS.has(String(path || "").split("?")[0]);
  }

  private mutationAuthorityValid(now = Date.now()) {
    const expiresAt = Date.parse(this.mutationAuthority?.expiresAt || "");
    return Boolean(
      this.mutationAuthority?.token
      && Number.isFinite(expiresAt)
      && expiresAt - 5000 > now
    );
  }

  private async ensureMutationAuthority(signal?: AbortSignal) {
    if (this.mutationAuthorityValid()) return this.mutationAuthority!;
    if (!this.mutationAuthorityRequest) {
      this.mutationAuthorityRequest = this.request<MobileMutationAuthorityResponse>("/mobile/mutation-authority", {
        method: "POST",
        signal,
        skipMobileMutation: true,
        body: JSON.stringify({})
      }).then((authority) => {
        this.mutationAuthority = authority;
        return authority;
      }).finally(() => {
        this.mutationAuthorityRequest = null;
      });
    }
    return this.mutationAuthorityRequest;
  }

  clearMutationAuthority() {
    this.mutationAuthority = null;
    this.mutationAuthorityRequest = null;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const {
      headers = {},
      timeoutMs = DEFAULT_TIMEOUT_MS,
      idempotencyKey,
      allowSuccessFalse = false,
      skipMobileMutation = false,
      signal: externalSignal,
      ...rest
    } = options;
    let mutation = this.needsMobileMutation(path, String(rest.method || "GET"), skipMobileMutation)
      ? await this.ensureMutationAuthority(externalSignal ?? undefined)
      : null;
    const requestId = createIdempotencyKey("mobile-request");
    const managed = createSignal(timeoutMs, externalSignal);
    const multipartBody = typeof FormData !== "undefined" && rest.body instanceof FormData;
    try {
      for (let attempt = 0; ; attempt++) {
        const response = await this.fetchImpl(apiUrl(path, this.apiPrefix), {
          credentials: "include",
          ...rest,
          signal: managed.signal,
          headers: {
            Accept: "application/json",
            ...(rest.body && !multipartBody ? { "Content-Type": "application/json" } : {}),
            ...headers,
            "X-EPIC-Request-Id": requestId,
            ...(this.mobileGateway ? { "X-EPIC-Mobile-Client": MOBILE_CLIENT } : {}),
            ...(mutation ? { "X-EPIC-Mobile-Mutation": mutation.token } : {}),
            ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {})
          }
        });
        const data = await response.json().catch(() => null);
        if (!data || typeof data !== "object" || Array.isArray(data)) {
          const unavailable = this.mobileGateway && response.status === 404;
          const safe = normalizeSafeError(response, {
            code: unavailable ? "MOBILE_API_UNAVAILABLE" : "API_RESPONSE_INVALID",
            message: unavailable
              ? "The mobile service is not available on the server yet. Please try again later."
              : "The server returned an unexpected response. Please try again.",
            retryable: true
          });
          throw Object.assign(new Error(safe.message), safe);
        }
        if (!response.ok || data?.ok === false || (!allowSuccessFalse && data?.success === false)) {
          const safe = normalizeSafeError(response, data);
          // Native background work can rotate this session's mutation token.
          // This exact rejection occurs before mutation execution, so one retry
          // with renewed authority is safe only for the original account.
          if (attempt === 0 && mutation && safe.status === 403 && safe.code === "MOBILE_MUTATION_AUTHORITY_REQUIRED") {
            const originalAccount = mutation.accountUsername;
            if (this.mutationAuthority?.token === mutation.token) this.mutationAuthority = null;
            const renewed = await this.ensureMutationAuthority(managed.signal);
            if (!originalAccount || renewed.accountUsername !== originalAccount) {
              throw Object.assign(new Error("The active account changed. Refresh before retrying this action."), {
                ...safe, code: "MOBILE_ACCOUNT_CONTEXT_CHANGED", message: "The active account changed. Refresh before retrying this action.", retryable: false
              });
            }
            mutation = renewed;
            continue;
          }
          throw Object.assign(new Error(safe.message), safe);
        }
        return data as T;
      }
    } catch (error: any) {
      if (error?.name === "AbortError") {
        const safe: SafeError = {
          status: 0,
          code: managed.timedOut() ? "REQUEST_TIMEOUT" : "REQUEST_CANCELLED",
          message: managed.timedOut() ? "Request timed out." : "Request was cancelled.",
          retryable: managed.timedOut(),
          requiredAction: null,
          correlationRef: null
        };
        throw Object.assign(new Error(safe.message), safe);
      }
      throw error;
    } finally {
      managed.cleanup();
    }
  }

  getSession(signal?: AbortSignal) {
    return this.request<MobileSession>("/accounts/me", { method: "GET", signal });
  }

  // Guest authority is its own signed cookie and CSRF token. It must never
  // request or attach the private account's mobile mutation authority.
  guestRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
    if (!MOBILE_GUEST_ENABLED) return Promise.reject(new Error("Guest chat is not enabled in this release."));
    const method = String(options.method || "GET").toUpperCase();
    const allowed = ((method === "GET" || method === "POST") && path === "/olivia/guest/session")
      || (method === "POST" && path === "/olivia/guest/chat")
      || (method === "GET" && (path === "/olivia/guest/history"
        || /^\/olivia\/guest\/chat\/[A-Za-z0-9_-]{8,128}\/status$/.test(path)));
    if (!allowed) return Promise.reject(new Error("Guest API destination is not allowed."));
    const headers = new Headers(options.headers);
    if (["Authorization", "X-EPIC-Mobile-Mutation", "X-EPIC-Account"].some(key => headers.has(key))) {
      return Promise.reject(new Error("Private authority is not allowed in guest requests."));
    }
    return this.request<T>(this.mobileGateway ? path.replace("/olivia/guest", "/olivia/native-guest") : path,
      { ...options, skipMobileMutation: true });
  }

  getMobileUi(signal?: AbortSignal) {
    return this.request<MobileUiResponse>("/mobile/ui", {
      method: "GET",
      signal,
      headers: { "X-EPIC-Mobile-Ui-Accept": "mobile-ui.v2, mobile-ui.v1" }
    });
  }

  getAuthConfig(signal?: AbortSignal) {
    return this.request<MobileAuthConfigResponse>("/auth/config", { method: "GET", signal });
  }

  signIn(username: string, password: string, signal?: AbortSignal) {
    return this.request<MobileSignInResponse>("/login", {
      method: "POST",
      signal,
      allowSuccessFalse: true,
      body: JSON.stringify({ username, password, returnTo: "/mobile/" })
    });
  }

  signInWithGoogle(credential: string, signal?: AbortSignal) {
    return this.request<MobileSignInResponse>("/google-login", {
      method: "POST",
      signal,
      allowSuccessFalse: true,
      body: JSON.stringify({ credential, returnTo: "/mobile/" })
    });
  }

  verifySecondFactor(code: string, signal?: AbortSignal) {
    return this.request<MobileSignInResponse>("/login/two-factor", {
      method: "POST",
      signal,
      body: JSON.stringify({ code })
    });
  }

  async logout(signal?: AbortSignal) {
    try {
      return await this.request<{ success?: boolean; ok?: boolean }>("/logout", { method: "POST", signal });
    } finally {
      this.clearMutationAuthority();
    }
  }

  exchangeNativeAuth(
    code: string,
    platform: "ios" | "android",
    client: "android-release" | "android-dev" | "ios-release",
    signal?: AbortSignal
  ) {
    return this.request<{ success: true; activeAccount: { username: string } }>("/mobile/auth/exchange", {
      method: "POST",
      signal,
      body: JSON.stringify({ code, platform, client })
    });
  }

  createWctekSession(signal?: AbortSignal) {
    return this.request<WctekWorkspaceResponse>("/olivia/workspace/sessions", {
      method: "POST",
      signal
    });
  }

  listWctekSessions(signal?: AbortSignal) {
    return this.request<WctekSessionListResponse>("/olivia/workspace/sessions", {
      method: "GET",
      signal
    });
  }

  getWctekSession(sessionId: string, signal?: AbortSignal) {
    return this.request<WctekWorkspaceResponse>(`/olivia/workspace/sessions/${encodeURIComponent(sessionId)}`, {
      method: "GET",
      signal
    });
  }

  sendWctekMessage(
    input: { message: string; clientMessageId: string; threadId: string; sessionId: string },
    signal?: AbortSignal
  ) {
    return this.request<WctekChatResponse>("/olivia/workspace/chat", {
      method: "POST",
      signal,
      timeoutMs: 90000,
      idempotencyKey: input.clientMessageId,
      body: JSON.stringify({
        message: input.message,
        clientMessageId: input.clientMessageId,
        threadId: input.threadId,
        sessionId: input.sessionId
      })
    });
  }

  getCompanionSettings(signal?: AbortSignal) {
    return this.request<AccountAiProfileSettingsResponse>("/wynter/ai-manager/profile-settings", {
      method: "GET",
      signal
    });
  }

  saveCompanionSettings(
    settings: Record<string, unknown>,
    options: { acceptPolicies?: boolean; signal?: AbortSignal } = {}
  ) {
    return this.request<AccountAiProfileSettingsResponse>("/wynter/ai-manager/profile-settings", {
      method: "PUT",
      signal: options.signal,
      body: JSON.stringify({
        settings,
        ...(options.acceptPolicies ? { policyAcceptance: { accepted: true } } : {})
      })
    });
  }

  getSocialLinks(accountUsername: string, signal?: AbortSignal) {
    return this.request<SocialLinksResponse>(`/accounts/${encodeURIComponent(accountUsername)}/social-links`, {
      method: "GET",
      signal
    });
  }

  getPublicProfile(accountUsername: string, signal?: AbortSignal) {
    return this.request<{ ok: true; profile: unknown }>(`/accounts/profile/${encodeURIComponent(accountUsername)}`, { method: "GET", signal });
  }

  saveSocialLinks(
    accountUsername: string,
    payload: { enabled: boolean; socialLinks: Array<Record<string, unknown>>; expectedRevision: number },
    options: { csrfToken: string; signal?: AbortSignal }
  ) {
    return this.request<SocialLinksResponse>(`/accounts/${encodeURIComponent(accountUsername)}/social-links`, {
      method: "PUT",
      signal: options.signal,
      headers: { "X-EPIC-CSRF": options.csrfToken },
      body: JSON.stringify(payload)
    });
  }

  getCheckinPreferences(signal?: AbortSignal) {
    return this.request<CheckinPreferencesResponse>("/olivia/checkins/preferences", { method: "GET", signal });
  }

  saveCheckinPreferences(patch: Record<string, unknown>, signal?: AbortSignal) {
    return this.request<CheckinPreferencesResponse>("/olivia/checkins/preferences", {
      method: "PUT",
      signal,
      body: JSON.stringify(patch)
    });
  }

  resetCheckinPreferences(signal?: AbortSignal) {
    return this.request<CheckinPreferencesResponse>("/olivia/checkins/preferences/reset", {
      method: "POST",
      signal
    });
  }

  listReviewDrafts(signal?: AbortSignal) {
    return this.request<MessagingReviewDraftsResponse>("/wynter/messaging/drafts", { method: "GET", signal });
  }

  saveReviewDraft(draftId: string, draftText: string, signal?: AbortSignal) {
    return this.request<MessagingReviewDraftResponse>(`/wynter/messaging/drafts/${encodeURIComponent(draftId)}`, {
      method: "PATCH",
      signal,
      body: JSON.stringify({ draftText })
    });
  }

  rejectReviewDraft(draftId: string, signal?: AbortSignal) {
    return this.request<MessagingReviewDraftResponse>(`/wynter/messaging/drafts/${encodeURIComponent(draftId)}/reject`, {
      method: "POST",
      signal
    });
  }

  takeOverReviewDraft(draftId: string, signal?: AbortSignal) {
    return this.request<MessagingReviewDraftResponse>(`/wynter/messaging/drafts/${encodeURIComponent(draftId)}/human-takeover`, {
      method: "POST",
      signal
    });
  }

  getWynterProfile(signal?: AbortSignal) {
    return this.request<WynterProfileResponse>("/wynter/profile", { method: "GET", signal });
  }

  saveWynterProfileConsent(
    consent: { styleLearningEnabled: boolean; conversationalUploadEnabled: boolean },
    signal?: AbortSignal
  ) {
    return this.request<WynterProfileResponse>("/wynter/profile/consent", {
      method: "POST",
      signal,
      body: JSON.stringify(consent)
    });
  }

  uploadConversationalData(file: File, signal?: AbortSignal) {
    const body = new FormData();
    body.set("consent", "true");
    body.set("purpose", "conversation_style_learning");
    body.set("file", file, file.name);
    return this.request<WynterProfileResponse>("/wynter/conversational-data/upload", {
      method: "POST",
      signal,
      body,
      timeoutMs: 120000
    });
  }

  listProviderConnections(signal?: AbortSignal) {
    return this.request<ProviderConnectionsResponse>("/platforms/connections", { method: "GET", signal });
  }

  executeProviderConnectionAction(
    targetRef: string,
    action: ProviderConnectionActionName,
    signal?: AbortSignal,
    setup?: import("../../public/shared/manager/textBeeGatewaySetup").TextBeeGatewaySetup
  ) {
    const idempotencyKey = createIdempotencyKey(`mobile-provider-${action}`);
    return this.request<ProviderConnectionActionResponse>(
      `/platforms/connections/${encodeURIComponent(targetRef)}/actions/${encodeURIComponent(action)}`,
      {
        method: "POST",
        signal,
        idempotencyKey,
        body: JSON.stringify({ idempotencyKey, confirmation: true, ...(setup ? { setup } : {}) })
      }
    );
  }

  listInboxConversations(options: ConversationListOptions = {}) {
    return this.request<InboxConversationPage>(
      appendParams("/inbox/conversations", {
        status: options.status || "open",
        folder: options.folder || "all",
        channel: options.channel || "all",
        search: options.search || null,
        sort: options.sort || "latest_inbound",
        labelIds: options.labelIds?.join(",") || null,
        view: options.view || "contacts",
        cursor: options.cursor || null,
        limit: options.limit || 20
      }),
      { method: "GET", signal: options.signal }
    );
  }

  listInboxFolders(signal?: AbortSignal) {
    return this.request<InboxFoldersResponse>("/inbox/folders", { method: "GET", signal });
  }

  listInboxLabels(signal?: AbortSignal) {
    return this.request<InboxLabelsResponse>("/inbox/labels", { method: "GET", signal });
  }

  getConversation(conversationRef: string, signal?: AbortSignal) {
    return this.request<InboxConversationDetail>(
      `/inbox/conversations/${encodeURIComponent(conversationRef)}`,
      { method: "GET", signal }
    );
  }

  getConversationContact(conversationRef: string, signal?: AbortSignal) {
    return this.request<InboxContactContextResponse>(
      `/inbox/conversations/${encodeURIComponent(conversationRef)}/contact`,
      { method: "GET", signal }
    );
  }

  listMessages(conversationRef: string, options: MessageListOptions = {}) {
    return this.request<InboxMessagePage>(
      appendParams(`/inbox/conversations/${encodeURIComponent(conversationRef)}/messages`, {
        cursor: options.cursor || null,
        limit: options.limit || 30
      }),
      { method: "GET", signal: options.signal }
    );
  }

  createInboxNote(conversationRef: string, text: string, idempotencyKey: string, signal?: AbortSignal) {
    return this.request<InboxComposerResponse>(
      `/inbox/conversations/${encodeURIComponent(conversationRef)}/notes`,
      {
        method: "POST",
        signal,
        idempotencyKey,
        body: JSON.stringify({ text })
      }
    );
  }

  createInboxReplyDraft(conversationRef: string, text: string, idempotencyKey: string, signal?: AbortSignal) {
    return this.request<InboxComposerResponse>(
      `/inbox/conversations/${encodeURIComponent(conversationRef)}/replies`,
      {
        method: "POST",
        signal,
        idempotencyKey,
        body: JSON.stringify({ text })
      }
    );
  }

  approveInboxDraft(
    conversationRef: string,
    messageRef: string,
    finalText: string,
    revision: string,
    idempotencyKey: string,
    signal?: AbortSignal
  ) {
    return this.request<InboxDraftReviewResponse>(
      `/inbox/conversations/${encodeURIComponent(conversationRef)}/drafts/${encodeURIComponent(messageRef)}/approve`,
      {
        method: "POST",
        signal,
        idempotencyKey,
        headers: { "If-Match": revision },
        body: JSON.stringify({ finalText })
      }
    );
  }

  rejectInboxDraft(
    conversationRef: string,
    messageRef: string,
    reasonCode: InboxDraftRejectionReason,
    revision: string,
    idempotencyKey: string,
    signal?: AbortSignal
  ) {
    return this.request<InboxDraftReviewResponse>(
      `/inbox/conversations/${encodeURIComponent(conversationRef)}/drafts/${encodeURIComponent(messageRef)}/reject`,
      {
        method: "POST",
        signal,
        idempotencyKey,
        headers: { "If-Match": revision },
        body: JSON.stringify({ reasonCode })
      }
    );
  }

  sendInboxDraft(
    conversationRef: string,
    messageRef: string,
    idempotencyKey: string,
    signal?: AbortSignal
  ) {
    return this.request<InboxDraftReviewResponse>(
      `/inbox/conversations/${encodeURIComponent(conversationRef)}/drafts/${encodeURIComponent(messageRef)}/send`,
      {
        method: "POST",
        signal,
        idempotencyKey,
        body: JSON.stringify({ confirmation: true })
      }
    );
  }
}
